
import { Shield, Heart, Users, Award, CheckCircle, ArrowRight } from 'lucide-react';
import { IslamicSymbol } from './ui/islamic-symbol';
import { NavigationBar } from "./shared/Navigation";
import { Footer } from "./shared/Footer";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

import { useLanguage } from '../contexts/LanguageContext';

interface AboutUsProps {
  onNavigate: (page: string) => void;
  onContactClick: () => void;
  onOpenTerms: () => void;
  onOpenPrivacy: () => void;
}

function HeroSection() {
  const { t } = useLanguage();
  return (
    <section className="bg-gradient-to-br from-[#f91bdc]/10 via-white to-[#f91bdc]/5 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              dangerouslySetInnerHTML={{ __html: t('about.title') }}>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('about.subtitle')}
          </p>
          <div className="flex items-center justify-center space-x-4 text-gray-500">
            <div className="flex items-center space-x-2">
              <Shield size={20} className="text-[#f91bdc]" />
              <span>{t('about.since2025')}</span>
            </div>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <div className="flex items-center space-x-2">
              <Users size={20} className="text-[#f91bdc]" />
              <span>{t('about.customers')}</span>
            </div>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <div className="flex items-center space-x-2">
              <IslamicSymbol size={20} className="text-[#f91bdc]" />
              <span>{t('about.scholarApproved')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionSection() {
  const { t } = useLanguage();
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('about.mission.title')}
          </h2>
          <div className="w-24 h-1 bg-[#f91bdc] mx-auto rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Card className="border-[#f91bdc]/20 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('about.mission.empowering')}
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {t('about.mission.description1')}
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t('about.mission.description2')}
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-6">
            {[
              {
                icon: <Heart className="w-6 h-6" />,
                title: t('about.mission.communityFocused'),
                description: t('about.mission.communityDesc')
              },
              {
                icon: <IslamicSymbol size={24} className="w-6 h-6" />,
                title: t('about.mission.scholarlyApproved'),
                description: t('about.mission.scholarlyDesc')
              },
              {
                icon: <CheckCircle className="w-6 h-6" />,
                title: t('about.mission.transparentProcess'),
                description: t('about.mission.transparentDesc')
              }
            ].map((item, index) => (
              <Card key={index} className="border-[#f91bdc]/10 hover:border-[#f91bdc]/30 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#f91bdc] text-white rounded-lg flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {item.title}
                      </h4>
                      <p className="text-gray-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const { t } = useLanguage();
  const values = [
    {
      title: t('about.values.islamicFirst'),
      description: t('about.values.islamicDesc'),
      icon: <IslamicSymbol size={32} className="w-8 h-8" />
    },
    {
      title: t('about.values.communityTrust'),
      description: t('about.values.communityDesc'),
      icon: <Heart className="w-8 h-8" />
    },
    {
      title: t('about.values.excellence'),
      description: t('about.values.excellenceDesc'),
      icon: <Award className="w-8 h-8" />
    },
    {
      title: t('about.values.innovation'),
      description: t('about.values.innovationDesc'),
      icon: <Users className="w-8 h-8" />
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('about.values.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('about.values.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {values.map((value, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-[#f91bdc]/10 hover:border-[#f91bdc]/30">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#f91bdc] text-white rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamSection() {
  const { t } = useLanguage();
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('about.whyChoose.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('about.whyChoose.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="border-[#f91bdc]/20 text-center">
            <CardHeader>
              <CardTitle className="text-[#f91bdc]">{t('about.whyChoose.experts')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                {t('about.whyChoose.expertsDesc')}
              </p>
            </CardContent>
          </Card>

          <Card className="border-[#f91bdc]/20 text-center">
            <CardHeader>
              <CardTitle className="text-[#f91bdc]">{t('about.whyChoose.technology')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                {t('about.whyChoose.technologyDesc')}
              </p>
            </CardContent>
          </Card>

          <Card className="border-[#f91bdc]/20 text-center">
            <CardHeader>
              <CardTitle className="text-[#f91bdc]">{t('about.whyChoose.community')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                {t('about.whyChoose.communityDesc')}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

export default function AboutUs({
  onNavigate,
  onContactClick,
  onOpenTerms,
  onOpenPrivacy
}: AboutUsProps) {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-white">
      <NavigationBar 
        onContactClick={onContactClick} 
      />
      
      <HeroSection />
      <MissionSection />
      <ValuesSection />
      <TeamSection />
      
      <section className="py-16 bg-gradient-to-br from-[#f91bdc]/10 via-white to-[#f91bdc]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {t('about.cta.title')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('about.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => onNavigate('listings')}
              className="bg-[#f91bdc] hover:bg-[#e015c9] text-white px-8 py-3 text-lg"
            >
              {t('about.cta.browseCars')} <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={onContactClick}
              className="border-[#f91bdc] text-[#f91bdc] hover:bg-[#f91bdc] hover:text-white px-8 py-3 text-lg"
            >
              {t('common.contactUs')}
            </Button>
          </div>
        </div>
      </section>

      <Footer 
        onOpenTerms={onOpenTerms} 
        onOpenPrivacy={onOpenPrivacy} 
      />
    </div>
  );
}